import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgPrincipleBridgeCloudModule } from '@principle-theorem/ng-principle-bridge-cloud';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  HicapsConnectMerchant,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IBridgeDevice,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  INamedDocument,
  IReffable,
  WithRef,
  undeletedFilter,
} from '@principle-theorem/shared';
import { ISelectedHicapsConnectTerminal } from '../../selected-hicaps-connect-terminal';
import { HicapsConnectV2Adapter } from '../hicaps-connect-v2-adapter';
import { IHicapsConnectRouteCandidate } from '../hicaps-connect-v2-routing';
import {
  HicapsConnectV2SyncService,
  IHicapsConnectV2SyncProgress,
} from '../hicaps-connect-v2-sync.service';
import {
  HicapsConnectV2TerminalMerchants,
  IHicapsConnectV2TerminalMerchant,
} from '../hicaps-connect-v2-terminal-merchants';

export const SYNC_NOTICE_MS = 3000;

export const PROGRESS_HEAD_PERCENT = 5;
export const PROGRESS_TAIL_PERCENT = 95;

export interface IHicapsV2SelectTerminalRequest {
  terminalMerchants: IHicapsConnectV2TerminalMerchant[];
  bridgeDevices: WithRef<IBridgeDevice>[];
  practice: INamedDocument<IPractice>;
  practitionersOnInvoice: IReffable<IStaffer>[];
}

export interface IHicapsV2SelectTerminalResponse {
  terminal: ISelectedHicapsConnectTerminal;
}

@Component({
  selector: 'pr-hicaps-connect-v2-select-terminal-dialog',
  templateUrl: './hicaps-connect-v2-select-terminal-dialog.component.html',
  styleUrls: ['./hicaps-connect-v2-select-terminal-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    NgPrincipleBridgeCloudModule,
    MatProgressBarModule,
    MatRadioModule,
    MatTooltipModule,
  ],
})
export class HicapsConnectV2SelectTerminalDialogComponent {
  private readonly _dialogRef =
    inject<
      MatDialogRef<
        HicapsConnectV2SelectTerminalDialogComponent,
        IHicapsV2SelectTerminalResponse
      >
    >(MatDialogRef);
  private readonly _data =
    inject<IHicapsV2SelectTerminalRequest>(MAT_DIALOG_DATA);
  private readonly _syncService = inject(HicapsConnectV2SyncService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _terminalMerchants = signal(this._data.terminalMerchants);
  private readonly _bridgeDevices = signal(this._data.bridgeDevices);

  readonly terminalGroups = computed(() =>
    HicapsConnectV2TerminalMerchants.toGroups(
      this._terminalMerchants(),
      this._bridgeDevices()
    )
  );
  readonly routableEntries = computed(() =>
    this.terminalGroups()
      .flatMap((group) => group.entries)
      .filter((entry) => !!entry.route)
  );
  readonly selectedPairId = signal<string | undefined>(undefined);
  readonly selectedEntry = computed(() =>
    this.routableEntries().find(
      (entry) => entry.entryId === this.selectedPairId()
    )
  );
  readonly selectedRoute = computed(() => this.selectedEntry()?.route);
  readonly overriddenBridgeId = signal<string | undefined>(undefined);
  readonly effectiveCandidate = computed(() => {
    const route = this.selectedRoute();
    if (!route) {
      return;
    }
    const overriddenCandidate = route.candidates.find(
      (candidate) => candidate.bridgeDevice.ref.id === this.overriddenBridgeId()
    );
    return overriddenCandidate ?? route.chosen;
  });
  readonly showFallbackWarning = computed(
    () => !this.overriddenBridgeId() && !!this.selectedRoute()?.usedFallback
  );
  readonly syncInProgress = signal(false);
  readonly syncProgress = signal<IHicapsConnectV2SyncProgress | undefined>(
    undefined
  );
  readonly syncPercentage = computed(() => {
    const progress = this.syncProgress();
    if (!progress || progress.total === 0) {
      return PROGRESS_HEAD_PERCENT;
    }
    const ratio = progress.completed / progress.total;
    return (
      PROGRESS_HEAD_PERCENT +
      ratio * (PROGRESS_TAIL_PERCENT - PROGRESS_HEAD_PERCENT)
    );
  });

  entrySelected(entryId: string): void {
    const isRoutable = this.routableEntries().some(
      (entry) => entry.entryId === entryId
    );
    if (!isRoutable) {
      return;
    }
    this.selectedPairId.set(entryId);
    this.overriddenBridgeId.set(undefined);
  }

  bridgeChanged(bridgeId: string): void {
    this.overriddenBridgeId.set(bridgeId);
  }

  confirmSelection(): void {
    const entry = this.selectedEntry();
    const candidate = this.effectiveCandidate();
    if (!entry || !candidate || this.syncInProgress()) {
      return;
    }
    const terminal = HicapsConnectV2Adapter.toSelectedTerminal(
      entry.terminal,
      entry.merchantId,
      entry.merchant,
      candidate
    );
    this._dialogRef.close({ terminal });
  }

  readonly isAutomaticCandidate = (
    candidate: IHicapsConnectRouteCandidate
  ): boolean => {
    const chosen = this.selectedRoute()?.chosen;
    return (
      !!chosen && chosen.bridgeDevice.ref.id === candidate.bridgeDevice.ref.id
    );
  };

  async sync(): Promise<void> {
    this.syncInProgress.set(true);
    try {
      await this._syncService.syncAllBridges(this._data.practice, (progress) =>
        this.syncProgress.set(progress)
      );
      await this._refreshData();
    } catch {
      this._snackBar.open('Sync failed', 'OK', { duration: SYNC_NOTICE_MS });
    } finally {
      this.syncInProgress.set(false);
      this.syncProgress.set(undefined);
    }
  }

  private async _refreshData(): Promise<void> {
    const [terminals, merchants, bridgeDevices] = await Promise.all([
      Firestore.getDocs(
        Practice.hicapsConnectTerminalV2Col(this._data.practice)
      ),
      Firestore.getDocs(Practice.hicapsConnectMerchantCol(this._data.practice)),
      Firestore.getDocs(Practice.bridgeDeviceCol(this._data.practice)),
    ]);
    const refreshedMerchants = merchants.filter(undeletedFilter);

    this._terminalMerchants.set(
      HicapsConnectV2TerminalMerchants.fromTerminals(
        terminals.filter(undeletedFilter),
        refreshedMerchants
      ).filter((terminalMerchant) =>
        HicapsConnectMerchant.isAvailableToPractitioners(
          terminalMerchant.merchant,
          this._data.practitionersOnInvoice
        )
      )
    );
    this._bridgeDevices.set(bridgeDevices);
  }
}
